// src/context/CartContext.js

import React, { createContext, useReducer, useEffect, useState } from 'react';

// --- Action Types ---
// Defines the types of actions that can be performed on the cart.
const ActionTypes = {
    ADD_TO_CART: 'ADD_TO_CART',
    REMOVE_FROM_CART: 'REMOVE_FROM_CART',
    UPDATE_QUANTITY: 'UPDATE_QUANTITY',
    CLEAR_CART: 'CLEAR_CART',
    SET_CART: 'SET_CART', // For initializing the cart from localStorage
};

// --- Reducer Function ---
// This function handles all the logic for updating the cart state.
const cartReducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.ADD_TO_CART: {
            const itemToAdd = action.payload;
            const existingItem = state.find(item => item.item_id === itemToAdd.item_id);

            if (existingItem) {
                return state.map(item =>
                    item.item_id === itemToAdd.item_id
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...state, { ...itemToAdd, quantity: 1 }];
        }

        case ActionTypes.REMOVE_FROM_CART: {
            return state.filter(item => item.item_id !== action.payload.itemId);
        }

        case ActionTypes.UPDATE_QUANTITY: {
            const { itemId, change } = action.payload;
            return state
                .map(item =>
                    item.item_id === itemId
                        ? { ...item, quantity: item.quantity + change }
                        : item
                )
                .filter(item => item.quantity > 0); // Remove item if quantity is 0 or less
        }

        case ActionTypes.CLEAR_CART: {
            return [];
        }

        case ActionTypes.SET_CART: {
            return action.payload;
        }

        default: {
            throw new Error(`Unhandled action type: ${action.type}`);
        }
    }
};

// --- Context Definition ---
// This creates the context object that components will use to access the state.
const CartContext = createContext();

// --- Provider Component ---
// This component will wrap our application and provide the cart state to all children.
const CartProvider = ({ children }) => {
    // Determine the initial restaurant ID synchronously from the URL if possible
    const getInitialRestaurantId = () => {
        const parts = window.location.pathname.split('/');
        if (parts.length >= 2 && parts[1] && !parts[1].startsWith('manage') && !parts[1].startsWith('superadmin') && !parts[1].startsWith('sponsor')) {
            return parts[1];
        }
        return null;
    };

    const initialId = getInitialRestaurantId();
    const [activeRestaurantId, setActiveRestaurantId] = useState(initialId);

    // Initialize state from localStorage or as an empty array, performing migration if necessary
    const getInitialCart = () => {
        if (!initialId) return [];
        try {
            const newKey = `restaurantCart_${initialId}`;
            let savedCart = localStorage.getItem(newKey);
            
            // Legacy Migration
            if (!savedCart) {
                const legacyCart = localStorage.getItem('restaurantCart');
                if (legacyCart) {
                    localStorage.setItem(newKey, legacyCart);
                    localStorage.removeItem('restaurantCart'); // Clean up old one
                    savedCart = legacyCart;
                }
            }
            return savedCart ? JSON.parse(savedCart) : [];
        } catch { return []; }
    };
    
    const [cart, dispatch] = useReducer(cartReducer, getInitialCart());
    
    // State for favorites and theme
    const [favorites, setFavorites] = useState(() => {
        if (!initialId) return [];
        try {
            const newKey = `restaurantFavorites_${initialId}`;
            let savedFavorites = localStorage.getItem(newKey);

            // Legacy Migration
            if (!savedFavorites) {
                const legacyFavorites = localStorage.getItem('restaurantFavorites');
                if (legacyFavorites) {
                    localStorage.setItem(newKey, legacyFavorites);
                    localStorage.removeItem('restaurantFavorites');
                    savedFavorites = legacyFavorites;
                }
            }
            return savedFavorites ? JSON.parse(savedFavorites) : [];
        } catch { return []; }
    });
    
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme === 'dark') {
            document.body.classList.add('dark-mode');
            return true;
        }
        return false;
    });

    // Handle switching between restaurants via React Router (SPA navigation)
    useEffect(() => {
        if (activeRestaurantId && activeRestaurantId !== initialId) {
            try {
                const savedCart = localStorage.getItem(`restaurantCart_${activeRestaurantId}`);
                dispatch({ type: ActionTypes.SET_CART, payload: savedCart ? JSON.parse(savedCart) : [] });

                const savedFavorites = localStorage.getItem(`restaurantFavorites_${activeRestaurantId}`);
                setFavorites(savedFavorites ? JSON.parse(savedFavorites) : []);
            } catch {
                dispatch({ type: ActionTypes.SET_CART, payload: [] });
                setFavorites([]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeRestaurantId]); // initialId is constant for the session

    // Save cart to localStorage whenever it changes
    useEffect(() => {
        if (activeRestaurantId) {
            localStorage.setItem(`restaurantCart_${activeRestaurantId}`, JSON.stringify(cart));
        }
    }, [cart, activeRestaurantId]);

    // Save favorites to localStorage whenever they change
    useEffect(() => {
        if (activeRestaurantId) {
            localStorage.setItem(`restaurantFavorites_${activeRestaurantId}`, JSON.stringify(favorites));
        }
    }, [favorites, activeRestaurantId]);

    // --- Handler for Theme Toggle ---
    const toggleTheme = () => {
        const newIsDarkMode = !isDarkMode;
        setIsDarkMode(newIsDarkMode);
        if (newIsDarkMode) {
            document.body.classList.add('dark-mode');
            localStorage.setItem('theme', 'dark');
        } else {
            document.body.classList.remove('dark-mode');
            localStorage.setItem('theme', 'light');
        }
    };
    
    // --- Handler for Favorites ---
    const toggleFavorite = (itemId) => {
        setFavorites(prev => 
            prev.includes(itemId) 
                ? prev.filter(id => id !== itemId) 
                : [...prev, itemId]
        );
    };

    // --- Context Value ---
    // This is the value that will be available to all consuming components.
    const value = { 
        cart, 
        dispatch, 
        ActionTypes,
        favorites,
        toggleFavorite,
        isDarkMode,
        toggleTheme,
        activeRestaurantId,
        setActiveRestaurantId
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export { CartContext, CartProvider, ActionTypes };